import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './context.js';
import { useI18n } from '../i18n/context.js';

/**
 * Защита разделов под `/app`. Пока идёт восстановление сессии, редиректить
 * нельзя: иначе обновление страницы выкидывало бы вошедшего на форму входа
 * (E2-T3, DoD «обновление страницы не разлогинивает»).
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { user, isBootstrapping } = useAuth();
  const location = useLocation();

  if (isBootstrapping) return <RestoringSession />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * The mirror of `RequireAuth` for sign-in and sign-up: a visitor who has a session is
 * sent where signing in takes them, not shown the form again. It is also what moves a
 * visitor on after signing in here. It waits for the first session check, or a
 * signed-in visitor would see the form flash first, but not for the retries: while the
 * refresh keeps failing (a restart, a rate limit, a blocking proxy) a guest must still
 * be able to sign in, and a session that comes back redirects from the form all the same.
 * Without a `from`, the query (`?plan=` from the pricing page) goes along, so My Books
 * still starts the next book on the plan that was picked.
 */
export function GuestOnly({ children }: { children: ReactNode }): JSX.Element {
  const { user, isBootstrapping, isReconnecting } = useAuth();
  const location = useLocation();

  if (isBootstrapping && !isReconnecting) return <RestoringSession />;

  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? `/app${location.search}`} replace />;
  }

  return <>{children}</>;
}

function RestoringSession(): JSX.Element {
  const { isReconnecting } = useAuth();
  const { t } = useI18n();
  return (
    <main className="flex min-h-screen items-center justify-center">
      <p role="status">{isReconnecting ? t.common.reconnecting : t.common.loading}</p>
    </main>
  );
}
